import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgePercent, FileText, ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";

const SCHEME_TYPE_LABELS = {
  slab_free_goods: "Slab / Free goods",
  cash_discount: "Cash discount",
  target_bonus: "Target bonus",
  annual_incentive: "Annual incentive",
  display_support: "Display support",
  other: "Other",
};

const STATUS_STYLES = {
  active: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/40",
  achieved: "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-500/40",
  skipped: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700",
  expired: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-500/40",
};

const SchemeCard = ({ scheme, onSaved }) => {
  const [qty, setQty] = useState(scheme.progress?.current_qty ?? 0);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const daysLeft = scheme.days_left;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/schemes/${scheme.id}`, { current_qty: Number(qty) || 0 });
      toast.success("Progress saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/schemes/${scheme.id}`, { status });
      toast.success(`Marked ${status}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`scheme-card-${scheme.id}`} className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-sm font-mono font-semibold text-emerald-700 dark:text-emerald-300">
          {SCHEME_TYPE_LABELS[scheme.scheme_type] || scheme.scheme_type}
        </span>
        <span className={`rounded-md border px-2 py-0.5 text-sm font-mono font-semibold ${STATUS_STYLES[scheme.status] || STATUS_STYLES.active}`} data-testid={`scheme-status-${scheme.id}`}>
          {scheme.status}
        </span>
        {daysLeft != null && scheme.status === "active" && (
          <span
            data-testid={`scheme-daysleft-${scheme.id}`}
            className={`rounded-md px-2 py-0.5 text-sm font-mono font-bold ${
              daysLeft <= 7 ? "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-500/40 animate-pulse-subtle" : "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-500/40"
            }`}
          >
            {daysLeft < 0 ? "expired" : daysLeft === 0 ? "ends today" : `${daysLeft}d left`}
          </span>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 leading-snug">{scheme.title}</h3>
        <p className="text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400 mt-0.5">{scheme.vendor_name}</p>
      </div>

      {scheme.details && <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{scheme.details}</p>}

      {scheme.slabs && scheme.slabs.length > 0 && (
        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-3">
          <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-1.5">Slabs</div>
          <ul className="space-y-1 text-base text-slate-800 dark:text-slate-200">
            {scheme.slabs.map((sl, i) => (
              <li key={i} data-testid={`scheme-slab-${scheme.id}-${i}`}>
                Buy <span className="font-mono font-bold">{sl.buy ?? sl.buy_qty}</span> → get <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{sl.free ?? sl.free_qty}</span> free
              </li>
            ))}
          </ul>
        </div>
      )}

      {scheme.cash_discount && (
        <p className="text-base text-slate-800 dark:text-slate-200">
          💵 <span className="font-mono font-bold">{scheme.cash_discount.pct}%</span> extra cash discount
          {scheme.cash_discount.within_days ? ` within ${scheme.cash_discount.within_days} days` : ""}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2 pt-1">
        <div>
          <label className="block text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-1">Qty bought</label>
          <input
            data-testid={`scheme-progress-input-${scheme.id}`}
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-28 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <button
          data-testid={`scheme-save-${scheme.id}`}
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-sm font-bold text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save progress"}
        </button>
        {scheme.status === "active" && (
          <>
            <button data-testid={`scheme-achieved-${scheme.id}`} onClick={() => setStatus("achieved")} className="rounded-md border border-cyan-300 dark:border-cyan-500/40 px-3 py-2 text-sm font-semibold text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-500/10 transition-colors">
              Achieved
            </button>
            <button data-testid={`scheme-skip-${scheme.id}`} onClick={() => setStatus("skipped")} className="rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              Skip
            </button>
          </>
        )}
        {scheme.source_path && (
          <a data-testid={`scheme-source-${scheme.id}`} href={fileUrl(scheme.source_path)} target="_blank" rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline text-sm">
            <FileText className="h-4 w-4" /> Source doc
          </a>
        )}
        {scheme.event_id && (
          <button data-testid={`scheme-event-link-${scheme.id}`} onClick={() => navigate(`/events/${scheme.event_id}`)}
            className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 text-sm transition-colors">
            Event <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const Schemes = () => {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/schemes");
      setSchemes(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = schemes.filter((s) => s.status === "active");
  const rest = schemes.filter((s) => s.status !== "active");

  return (
    <div data-testid="schemes-page" className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Scheme Tracker</h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Slab free goods, cash discounts, target bonuses, display support — with validity countdown and your purchase progress.
        </p>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="schemes-loading">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
        </div>
      ) : schemes.length === 0 ? (
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center" data-testid="schemes-empty">
          <BadgePercent className="mx-auto h-8 w-8 text-slate-500 mb-2" />
          <p className="text-base text-slate-600 dark:text-slate-400">No schemes yet. When a scheme message is captured and confirmed, it appears here automatically.</p>
        </div>
      ) : (
        <>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Active ({active.length})</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="schemes-active-list">
              {active.map((s) => <SchemeCard key={s.id} scheme={s} onSaved={load} />)}
            </div>
          </div>
          {rest.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-3">Closed / skipped ({rest.length})</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="schemes-closed-list">
                {rest.map((s) => <SchemeCard key={s.id} scheme={s} onSaved={load} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Schemes;
